"use client";

import * as React from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { useLocale } from "@/content/i18n/context";
import { t } from "@/content/i18n/messages";
import { getLocalizedPathname } from "@/lib/i18n";
import { cn } from "@/lib/utils";

interface CTAButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  href?: string;
  target?: React.HTMLAttributeAnchorTarget;
  rel?: string;
  prefetch?: boolean;
  size?: 'default' | 'large';
  variant?: 'default' | 'inverse' | 'subtle' | 'outline';
  className?: string;
}

const CTAButton = React.forwardRef<HTMLButtonElement, CTAButtonProps>(
  (
    {
      children,
      href,
      onClick,
      size = 'default',
      variant = 'default',
      className,
      disabled,
      target,
      rel,
      prefetch,
      ...props
    },
    ref,
  ) => {
    const locale = useLocale();
    const variantClasses = {
      default: "bg-primary font-bold text-primary-foreground hover:bg-primary-strong focus:ring-2 focus:ring-primary focus:ring-offset-2",
      inverse: "bg-primary-contrast font-bold text-primary shadow-lg hover:bg-primary-contrast/90 focus:ring-2 focus:ring-primary-contrast focus:ring-offset-2",
      subtle: "border border-white/40 bg-white/10 font-semibold text-white shadow-sm transition hover:bg-white/20 focus:ring-2 focus:ring-white/70 focus:ring-offset-2 focus:ring-offset-transparent",
      outline: "bg-transparent border border-neutral-border font-semibold text-neutral-emphasis hover:bg-primary/5 hover:border-primary/40 focus:ring-2 focus:ring-primary focus:ring-offset-2",
    };

    const sizeClasses = {
      default: "px-6 py-3",
      large: "px-8 py-3"
    };

    const buttonClasses = cn(variantClasses[variant], sizeClasses[size], className);
    const isExternalHref = href ? /^https?:\/\//.test(href) || href.startsWith("#") : false;
    const localizedHref = href && !isExternalHref
      ? getLocalizedPathname(href, locale)
      : href;
    const translatedChildren = typeof children === "string"
      ? t(locale, children)
      : children;

    // If href is provided, wrap in Link
    if (localizedHref) {
      return (
        <Link href={localizedHref} prefetch={prefetch} target={target} rel={rel}>
          <Button
            ref={ref}
            className={buttonClasses}
            disabled={disabled}
            onClick={onClick}
            {...props}
          >
            {translatedChildren}
          </Button>
        </Link>
      );
    }

    // Otherwise, use as a regular button
    return (
      <Button
        ref={ref}
        onClick={onClick}
        className={buttonClasses}
        disabled={disabled}
        {...props}
    >
        {translatedChildren}
      </Button>
    );
  }
);

CTAButton.displayName = "CTAButton";

export { CTAButton };
